import React, { useState } from "react";

function getDashboardTablePages(page, pageCount) {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, index) => index + 1);
    if (page <= 4) return [1, 2, 3, 4, 5, "ellipsis-end", pageCount];
    if (page >= pageCount - 3) {
        return [1, "ellipsis-start", pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1, pageCount];
    }
    return [1, "ellipsis-start", page - 1, page, page + 1, "ellipsis-end", pageCount];
}

export function getBetaDashboardStatusTone(status) {
    const value = String(status || "")
        .trim()
        .toLowerCase()
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ");

    if ([
        "active",
        "approved",
        "complete",
        "completed",
        "ended",
        "live",
        "published",
        "ready",
        "ready for review",
        "restored",
        "scheduled",
        "uploaded",
        "all scheduled",
    ].includes(value)) {
        return "success";
    }
    if ([
        "cancelled",
        "error",
        "failed",
        "missing",
        "overdue",
        "rejected",
    ].some((term) => value.includes(term)) || ["issue", "issues"].includes(value)) {
        return "danger";
    }
    if ([
        "archiving",
        "awaiting review",
        "pending approval",
        "queued",
        "restoring",
        "retry needed",
        "waiting lobby",
    ].includes(value)) {
        return "warning";
    }
    if (["requested"].includes(value)) return "purple";
    if (["in progress", "planned", "processing", "recording", "starting", "uploading"].includes(value)) {
        return "info";
    }
    return "neutral";
}

export function getBetaToneAccent(tone) {
    if (tone === "success") return "#16a34a";
    if (tone === "danger") return "#dc2626";
    if (tone === "warning") return "#d97706";
    if (tone === "info") return "#168cf2";
    if (tone === "purple") return "#7c5ac7";
    if (tone === "closed") return "#4f46a5";
    if (tone === "surface") return "var(--beta-border-strong)";
    return "#8a8a8a";
}

export function getBetaStatusAccent(status) {
    return getBetaToneAccent(getBetaDashboardStatusTone(status));
}

export function BetaDashboardStatus({ label, tone = getBetaDashboardStatusTone(label) }) {
    return (
        <span className={`beta-dashboard-status beta-dashboard-status--${tone}`}>
            {label || "—"}
        </span>
    );
}

export function BetaDashboardStats({ items }) {
    return (
        <section className="beta-dashboard-stats" aria-label="Dashboard totals">
            {items.map((item) => (
                <div className="beta-dashboard-stat" key={item.label}>
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                    {item.description ? <small>{item.description}</small> : null}
                </div>
            ))}
        </section>
    );
}

export function BetaDashboardSection({ title, description, badge = null, children }) {
    return (
        <section className="beta-dashboard-section">
            <header className="beta-dashboard-section__header">
                <div>
                    <h3>{title}</h3>
                    {description ? <p>{description}</p> : null}
                </div>
                {badge ? <BetaDashboardStatus {...badge} /> : null}
            </header>
            <div className="beta-dashboard-section__body">{children}</div>
        </section>
    );
}

export function BetaDashboardTable({
    ariaLabel,
    columns = ["Episode", "Status", "Date"],
    rows,
    emptyMessage,
    itemLabel = "items",
}) {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    if (!rows.length) {
        return <div className="beta-dashboard-table__empty">{emptyMessage}</div>;
    }

    const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
    const currentPage = Math.min(page, pageCount);
    const firstResult = (currentPage - 1) * pageSize + 1;
    const lastResult = Math.min(currentPage * pageSize, rows.length);
    const pageRows = rows.slice(firstResult - 1, lastResult);

    const changePageSize = (nextPageSize) => {
        setPageSize(nextPageSize);
        setPage(1);
    };

    return (
        <div className="beta-dashboard-table" role="group" aria-label={ariaLabel}>
            <div className="beta-dashboard-table__scroll">
                <table>
                    <thead>
                        <tr>
                            {columns.map((column) => <th scope="col" key={column}>{column}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.map((row) => {
                            const interactive = typeof row.onClick === "function";
                            const statusTone = row.statusTone || getBetaDashboardStatusTone(row.status);
                            return (
                                <tr
                                    key={row.key}
                                    className={interactive ? "is-interactive" : undefined}
                                    style={{ "--beta-dashboard-row-accent": getBetaToneAccent(statusTone) }}
                                    onClick={row.onClick}
                                    onKeyDown={interactive ? (event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            row.onClick();
                                        }
                                    } : undefined}
                                    tabIndex={interactive ? 0 : undefined}
                                >
                                    <td className="beta-dashboard-table__identity">
                                        <strong title={row.title}>{row.title || "—"}</strong>
                                        {row.subtitle ? <span title={row.subtitle}>{row.subtitle}</span> : null}
                                        {row.detail ? <small title={row.detail}>{row.detail}</small> : null}
                                    </td>
                                    <td><BetaDashboardStatus label={row.status} tone={statusTone} /></td>
                                    <td className="beta-dashboard-table__date">
                                        <span>{row.date || "—"}</span>
                                        {row.dateDetail ? <small>{row.dateDetail}</small> : null}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <footer className="beta-episode-table__footer">
                <span>{firstResult}–{lastResult} of {rows.length} {rows.length === 1 ? itemLabel.replace(/s$/, "") : itemLabel}</span>
                <label>
                    <span className="sr-only">Rows per page</span>
                    <select value={pageSize} onChange={(event) => changePageSize(Number(event.target.value))}>
                        {[10, 25, 50].map((size) => <option key={size} value={size}>{size}</option>)}
                    </select>
                </label>
                <nav aria-label={`${ariaLabel} pages`}>
                    <button type="button" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1}>‹</button>
                    {getDashboardTablePages(currentPage, pageCount).map((item) => typeof item === "string" ? (
                        <span key={item}>…</span>
                    ) : (
                        <button
                            key={item}
                            type="button"
                            className={item === currentPage ? "is-active" : ""}
                            onClick={() => setPage(item)}
                        >
                            {item}
                        </button>
                    ))}
                    <button type="button" onClick={() => setPage(currentPage + 1)} disabled={currentPage === pageCount}>›</button>
                </nav>
            </footer>
        </div>
    );
}
